import { memo, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { lazily } from 'react-lazily';

import { checkBirthDate } from '@login/helpers/birth-date';
import {
	getDayOptionsForMonthYear,
	getLocaleMonthOptions,
	getYearsOptions,
} from '@login/helpers/options';

import styles from './date-of-birth-fields.module.scss';

const { Select, Hint } = lazily(() => import('core/generic/elements/form'));

export type DateOfBirthLabels = {
	legend: string;
	hint: string;
	dayPlaceholder: string;
	monthPlaceholder: string;
	yearPlaceholder: string;
	dayRequired: string;
	monthRequired: string;
	yearRequired: string;
	tooYoung: string;
	invalid?: string;
	/** Optional explanatory copy rendered under the selects. */
	message?: string;
};

export type DateOfBirthEnabled = {
	day?: boolean;
	month?: boolean;
	year?: boolean;
};

type ResolveFieldError = (formMessage?: string, apiKey?: string) => string;

type DateOfBirthFieldsProps = {
	labels: DateOfBirthLabels;
	minimumAge?: number;
	/** Renders the leading asterisk and marks the selects required. */
	showRequiredMarker?: boolean;
	enabled?: DateOfBirthEnabled;
	/** Merges API field messages in; see useIdmFormErrors#getFieldError. */
	resolveError?: ResolveFieldError;
};

const ALL_ENABLED: DateOfBirthEnabled = { day: true, month: true, year: true };

const INVALID_BIRTH_DATE_MESSAGE = 'Invalid birth date';

/**
 * The three birth-date selects, validated together as one date.
 *
 * This component owns its `useWatch` calls, so changing day, month or year
 * re-renders only this subtree. Each form previously watched those fields at
 * its root, which meant one select re-rendered the whole ~1,000-line form.
 */
const DateOfBirthFieldsInner = ({
	labels,
	minimumAge,
	showRequiredMarker = false,
	enabled = ALL_ENABLED,
	resolveError,
}: DateOfBirthFieldsProps) => {
	const { i18n } = useTranslation();
	const {
		control,
		register,
		trigger,
		setValue,
		setError,
		clearErrors,
		formState: { errors },
	} = useFormContext();

	const selectedDay = useWatch({ control, name: 'dayOfBirth' });
	const selectedMonth = useWatch({ control, name: 'monthOfBirth' });
	const selectedYear = useWatch({ control, name: 'yearOfBirth' });

	const dayOptions = getDayOptionsForMonthYear(selectedMonth, selectedYear);
	const dayCount = dayOptions.length;

	// Clamp the day when a shorter month is picked, then re-check the age.
	useEffect(() => {
		if (Number(selectedDay) > dayCount) {
			setValue('dayOfBirth', '1');
			return;
		}

		const result = checkBirthDate(
			{
				dayOfBirth: selectedDay,
				monthOfBirth: selectedMonth,
				yearOfBirth: selectedYear,
			},
			minimumAge
		);

		if (result.status === 'invalid') {
			setError('birthDate', {
				message: labels.invalid ?? INVALID_BIRTH_DATE_MESSAGE,
			});
			return;
		}

		if (result.status === 'under-age') {
			setError('birthDate', { message: labels.tooYoung });
			return;
		}

		clearErrors('birthDate');
	}, [
		selectedDay,
		selectedMonth,
		selectedYear,
		dayCount,
		minimumAge,
		labels.invalid,
		labels.tooYoung,
		setValue,
		setError,
		clearErrors,
	]);

	const errorFor = (formMessage?: string, apiKey?: string) =>
		resolveError?.(formMessage, apiKey) ?? formMessage ?? '';

	return (
		<div className={styles.dateSection}>
			<div>
				<div className={`label-medium ${styles.labelPadding}`}>
					<p
						className={
							enabled.day ? undefined : styles.disabledText
						}
					>
						{showRequiredMarker && (
							<span className={styles.redText}>*</span>
						)}
						{labels.legend}
					</p>
					<Hint text={labels.hint} />
				</div>

				<div className={styles.selectWrapper}>
					<Select
						{...register('dayOfBirth', {
							required: labels.dayRequired,
						})}
						name="dayOfBirth"
						control={control}
						trigger={trigger}
						id="dayOfBirth"
						label=""
						error={errorFor(
							errors.dayOfBirth?.message as string,
							'DayOfBirth'
						)}
						errorIcon="AlertCircleFilled"
						required={!!enabled.day && showRequiredMarker}
						disabled={!enabled.day}
						placeholder={labels.dayPlaceholder}
						options={dayOptions}
					/>
					<Select
						{...register('monthOfBirth', {
							required: labels.monthRequired,
						})}
						name="monthOfBirth"
						control={control}
						trigger={trigger}
						id="monthOfBirth"
						label=""
						error={errorFor(
							errors.monthOfBirth?.message as string,
							'MonthOfBirth'
						)}
						errorIcon="AlertCircleFilled"
						required={!!enabled.month && showRequiredMarker}
						disabled={!enabled.month}
						placeholder={labels.monthPlaceholder}
						options={getLocaleMonthOptions(i18n.language)}
					/>
					<Select
						{...register('yearOfBirth', {
							required: labels.yearRequired,
						})}
						name="yearOfBirth"
						control={control}
						trigger={trigger}
						id="yearOfBirth"
						label=""
						error={errorFor(
							errors.yearOfBirth?.message as string,
							'YearOfBirth'
						)}
						errorIcon="AlertCircleFilled"
						required={!!enabled.year && showRequiredMarker}
						disabled={!enabled.year}
						placeholder={labels.yearPlaceholder}
						options={getYearsOptions()}
					/>
				</div>
			</div>

			{labels.message && (
				<div className={`body-small ${styles.contentSecondaryText}`}>
					{labels.message}
				</div>
			)}
		</div>
	);
};

export const DateOfBirthFields = memo(DateOfBirthFieldsInner);

export default DateOfBirthFields;
